import { FC, useState } from "react"
import {
  ContactFormDetails,
  ContactFormField,
  ContactFormFieldType
} from "../../../../../../../../types"
import BBSwitch from "../../../../../../form/Switch"

interface Props {
  values: ContactFormDetails
  setConfigField: (field: string, value: any) => void
}

// ─── Field type metadata ───────────────────────────────────────────────────────

const FIELD_TYPE_META: Record<
  ContactFormFieldType,
  { label: string; color: string; icon: string }
> = {
  text: {
    label: "Text",
    color: "bg-blue-50 text-blue-600 border-blue-100",
    icon: "Aa"
  },
  email: {
    label: "Email",
    color: "bg-violet-50 text-violet-600 border-violet-100",
    icon: "@"
  },
  tel: {
    label: "Phone",
    color: "bg-green-50 text-green-600 border-green-100",
    icon: "☎"
  },
  number: {
    label: "Number",
    color: "bg-amber-50 text-amber-600 border-amber-100",
    icon: "#"
  },
  select: {
    label: "Select",
    color: "bg-orange-50 text-orange-600 border-orange-100",
    icon: "⌄"
  },
  location_autocomplete: {
    label: "Location",
    color: "bg-rose-50 text-rose-600 border-rose-100",
    icon: "📍"
  },
  lo_select: {
    label: "LO Select",
    color: "bg-pink-50 text-pink-600 border-pink-100",
    icon: "👤"
  },
  checkbox: {
    label: "Checkbox",
    color: "bg-teal-50 text-teal-600 border-teal-100",
    icon: "☑"
  },
  textarea: {
    label: "Textarea",
    color: "bg-gray-50 text-gray-500 border-gray-200",
    icon: "¶"
  }
}

const FIELD_TYPES = Object.keys(FIELD_TYPE_META) as ContactFormFieldType[]

// ─── Shared primitives ─────────────────────────────────────────────────────────

const FieldLabel: FC<{ label: string }> = ({ label }) => (
  <label className="block text-xs font-medium text-gray-600 mb-1">
    {label}
  </label>
)

const TextInput: FC<{
  value: string
  onChange: (v: string) => void
  placeholder?: string
  disabled?: boolean
}> = ({ value, onChange, placeholder, disabled }) => (
  <input
    type="text"
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    disabled={disabled}
    className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
  />
)

// ─── Options editor (for select type) ─────────────────────────────────────────

const OptionsEditor: FC<{
  options: string[]
  onChange: (options: string[]) => void
}> = ({ options, onChange }) => {
  const addOption = () => onChange([...options, ""])
  const updateOption = (i: number, val: string) => {
    const next = [...options]
    next[i] = val
    onChange(next)
  }
  const removeOption = (i: number) =>
    onChange(options.filter((_, idx) => idx !== i))

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-gray-700">Options</p>
        <span className="text-xs text-gray-400">{options.length} items</span>
      </div>
      {options.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-3 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          No options yet
        </p>
      )}
      <div className="space-y-1.5">
        {options.map((opt, i) => (
          <div
            key={i}
            className="group flex items-center gap-2 p-2 rounded-xl bg-white border border-gray-100 hover:border-gray-200 transition-all"
          >
            <input
              value={opt}
              onChange={e => updateOption(i, e.target.value)}
              placeholder="Option value"
              className="flex-1 text-xs px-2.5 py-1.5 bg-gray-50 border border-gray-100 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
            <button
              onClick={() => removeOption(i)}
              className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-400 transition-all text-lg leading-none"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={addOption}
        className="mt-2 w-full py-2 text-xs font-medium text-indigo-500 hover:text-indigo-600 border border-dashed border-indigo-200 hover:border-indigo-400 rounded-xl transition-all"
      >
        + Add option
      </button>
    </div>
  )
}

// ─── Field card ────────────────────────────────────────────────────────────────

interface FieldCardProps {
  field: ContactFormField
  index: number
  total: number
  isExpanded: boolean
  onToggle: () => void
  onUpdate: (updated: ContactFormField) => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}

const FieldCard: FC<FieldCardProps> = ({
  field,
  index,
  total,
  isExpanded,
  onToggle,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown
}) => {
  const meta = FIELD_TYPE_META[field.type] ?? {
    label: field.type,
    color: "bg-gray-100 text-gray-500 border-gray-200",
    icon: "?"
  }
  const isSelect = field.type === "select"
  const set = <K extends keyof ContactFormField>(
    k: K,
    v: ContactFormField[K]
  ) => onUpdate({ ...field, [k]: v })

  return (
    <div
      className={`rounded-2xl border transition-all duration-200 ${
        isExpanded
          ? "border-indigo-200 shadow-md shadow-indigo-50"
          : "border-gray-100 hover:border-gray-200 hover:shadow-sm"
      }`}
    >
      {/* Header row */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-center gap-3 p-3.5 text-left min-w-0"
        >
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs text-gray-400 font-semibold shrink-0">
            {index + 1}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">
              {field.label || "(unlabeled)"}
            </p>
            <p className="text-xs text-gray-400 font-mono">{field.id}</p>
          </div>
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full border shrink-0 ${meta.color}`}
          >
            {meta.icon} {meta.label}
          </span>
          <span
            className={`text-gray-400 text-sm transition-transform duration-200 shrink-0 ${
              isExpanded ? "rotate-180" : ""
            }`}
          >
            ▾
          </span>
        </button>

        {/* Move buttons */}
        <div className="flex flex-col pr-2 gap-0.5">
          <button
            type="button"
            disabled={index === 0}
            onClick={onMoveUp}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 text-gray-300 hover:text-gray-600 disabled:opacity-20 disabled:cursor-not-allowed text-xs transition-colors"
          >
            ▲
          </button>
          <button
            type="button"
            disabled={index === total - 1}
            onClick={onMoveDown}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 text-gray-300 hover:text-gray-600 disabled:opacity-20 disabled:cursor-not-allowed text-xs transition-colors"
          >
            ▼
          </button>
        </div>
      </div>

      {/* Expanded editor */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-4">
          {/* ID (readonly) + Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel label="Field ID" />
              <TextInput
                value={field.id}
                onChange={v => set("id", v)}
                placeholder="field_id"
              />
            </div>
            <div>
              <FieldLabel label="Type" />
              <select
                value={field.type}
                onChange={e =>
                  set("type", e.target.value as ContactFormFieldType)
                }
                className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              >
                {FIELD_TYPES.map(t => (
                  <option key={t} value={t}>
                    {FIELD_TYPE_META[t].label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Label */}
          <div>
            <FieldLabel label="Label" />
            <TextInput
              value={field.label}
              onChange={v => set("label", v)}
              placeholder="Field label shown to user"
            />
          </div>

          {/* Description */}
          <div>
            <FieldLabel label="Description" />
            <TextInput
              value={field.description}
              onChange={v => set("description", v)}
              placeholder="Optional helper text (optional)"
            />
          </div>

          {/* Required toggle */}
          <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 rounded-xl">
            <p className="text-sm font-medium text-gray-700">Required</p>
            <BBSwitch
              enabled={field.required}
              setEnabled={v => set("required", v)}
            />
          </div>

          {/* Options editor — only for select type */}
          {isSelect && (
            <OptionsEditor
              options={field.options ?? []}
              onChange={opts => set("options", opts)}
            />
          )}

          {/* Remove */}
          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={onRemove}
              className="text-xs text-red-400 hover:text-red-500 font-medium transition-colors"
            >
              Remove field
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Default new field ─────────────────────────────────────────────────────────

const createField = (): ContactFormField => ({
  id: `field_${Date.now()}`,
  label: "",
  description: "",
  type: "text",
  required: false,
  options: []
})

// ─── Main component ────────────────────────────────────────────────────────────

const ContactFormFields: FC<Props> = ({ values, setConfigField }) => {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null)
  const fields: ContactFormField[] = values.config?.fields ?? []

  const setFields = (next: ContactFormField[]) => setConfigField("fields", next)

  const updateField = (i: number, updated: ContactFormField) => {
    const next = [...fields]
    next[i] = updated
    setFields(next)
  }

  const removeField = (i: number) => {
    setFields(fields.filter((_, idx) => idx !== i))
    if (expandedIdx === i) setExpandedIdx(null)
    else if (expandedIdx !== null && expandedIdx > i)
      setExpandedIdx(expandedIdx - 1)
  }

  const moveField = (i: number, dir: -1 | 1) => {
    const next = [...fields]
    const j = i + dir
    if (j < 0 || j >= next.length) return
    ;[next[i], next[j]] = [next[j], next[i]]
    setFields(next)
    setExpandedIdx(j)
  }

  const addField = () => {
    const newField = createField()
    setFields([...fields, newField])
    setExpandedIdx(fields.length)
  }

  return (
    <div className="pt-2 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">Form fields</p>
        <span className="text-xs text-gray-400">{fields.length} fields</span>
      </div>

      <div className="space-y-2">
        {fields.map((field, i) => (
          <FieldCard
            key={`${field.id}-${i}`}
            field={field}
            index={i}
            total={fields.length}
            isExpanded={expandedIdx === i}
            onToggle={() => setExpandedIdx(expandedIdx === i ? null : i)}
            onUpdate={updated => updateField(i, updated)}
            onRemove={() => removeField(i)}
            onMoveUp={() => moveField(i, -1)}
            onMoveDown={() => moveField(i, 1)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={addField}
        className="w-full py-3 text-sm font-medium text-indigo-500 hover:text-indigo-600 border border-dashed border-indigo-200 hover:border-indigo-400 rounded-2xl transition-all"
      >
        + Add field
      </button>
    </div>
  )
}

export default ContactFormFields
